import { useEffect, useCallback, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import { Keyboard } from '@capacitor/keyboard'
import { App as CapApp } from '@capacitor/app'
import { connectSocket, getSocket } from '@/helpers/socket'

interface UseCapacitorOptions {
  theme?: 'dark' | 'light'
  onBackButton?: () => boolean
}

export function useCapacitor({ theme = 'light', onBackButton }: UseCapacitorOptions = {}) {
  const isNative = Capacitor.isNativePlatform()
  const onBackButtonRef = useRef(onBackButton)
  onBackButtonRef.current = onBackButton

  const configureStatusBar = useCallback(
    async (currentTheme: 'dark' | 'light') => {
      if (!isNative) return

      try {
        await StatusBar.setStyle({
          style: currentTheme === 'dark' ? Style.Dark : Style.Light
        })

        if (Capacitor.getPlatform() === 'android') {
          await StatusBar.setBackgroundColor({
            color: currentTheme === 'dark' ? '#0B141A' : '#00A884'
          })
        }
      } catch {
        // StatusBar plugin not available on web
      }
    },
    [isNative]
  )

  useEffect(() => {
    if (!isNative) return

    const platform = Capacitor.getPlatform()

    // Add platform-specific class to body for CSS targeting
    document.body.classList.add('capacitor-app')
    document.body.classList.add(`platform-${platform}`)

    // For Android, add platform class
    if (platform === 'android') {
      document.body.classList.add('android-platform')
    }

    const init = async () => {
      await configureStatusBar(theme)

      // Clear caches on app start for native platforms to ensure fresh content
      try {
        // Clear any stale cache data
        if ('caches' in window) {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(async name => await caches.delete(name)))
        }
      } catch {
        // Cache API not available
      }

      try {
        await SplashScreen.hide()
      } catch {
        // SplashScreen plugin not available
      }
    }

    init()
  }, [isNative, theme, configureStatusBar])

  useEffect(() => {
    if (!isNative) return

    configureStatusBar(theme)
  }, [theme, isNative, configureStatusBar])

  useEffect(() => {
    if (!isNative) return

    const showListener = Keyboard.addListener('keyboardWillShow', info => {
      document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`)
      document.body.classList.add('keyboard-open')
    })

    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      document.documentElement.style.setProperty('--keyboard-height', '0px')
      document.body.classList.remove('keyboard-open')
    })

    return () => {
      void showListener.then(async l => await l.remove())
      void hideListener.then(async l => await l.remove())
    }
  }, [isNative])

  useEffect(() => {
    if (!isNative) return

    const listener = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive && !getSocket().connected) {
        const isLoggedIn = !!localStorage.getItem('devchat_app_userAuthenticated')
        if (isLoggedIn) connectSocket()
      }
    })

    return () => {
      void listener.then(async l => await l.remove())
    }
  }, [isNative])

  useEffect(() => {
    if (!isNative) return

    const listener = CapApp.addListener('backButton', ({ canGoBack }) => {
      const handled = onBackButtonRef.current?.()
      if (!handled) {
        if (!canGoBack) {
          CapApp.minimizeApp()
        } else {
          window.history.back()
        }
      }
    })

    return () => {
      void listener.then(async l => await l.remove())
    }
  }, [isNative])

  return { isNative, platform: Capacitor.getPlatform() }
}

export default useCapacitor
